import { Button, Col, Icon, Row, Skeleton, Tooltip, message } from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import classNames from 'classnames';
import moment from 'moment';
import * as React from 'react';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { rejectInquiryFF } from 'src/api/inquiry';
import ViewBreakdown from 'src/components/pages/Enquiry/Modals/ViewBreakdown';
import { commonStyles } from 'src/Library/CommonStyles';
import { InquiryModes } from 'src/Library/Constants';
import { isValidNumber, isValidString } from 'src/Library/Helper';
import { getInquiryDetailRoute, getInquiryFFRoute } from 'src/routing/utils';
import { nl2Br } from 'src/utils/DOMUtils';
import { EventCategories, sendEvent } from 'src/utils/gaService';
import CardNotification from '../../../common/cardNotification';
import PricePerContainer from '../../../common/PricePerContainer';
import { getDeadlineData, rejectInquiryShown } from '../InquiryUtils';
import FFInquiryRejectModal from '../Modals/FFInquiryRejectModal';
import FFRevokeInquiryModal from "../Modals/FFRevokeInquiryModal";
import ContainerDetails from './ContainerDetails/ContainerDetails';
import { getEmptyContainerLocation, renderInquiryDestinationDetails, renderInquiryOriginDetails } from './InquiryCommon';
import { tokens } from 'src/design-system';

const styles = (theme: any) => ({
  overlay: {
    zIndex: '1',
    backgroundColor: 'rgba(0,0,0, 0.9)',
    overflowX: 'hidden',
    transition: '0.5s',
  },
  circle: {
    height: '18px',
    width: '18px',
    borderRadius: '9px',
    backgroundColor: '#424242',
    textAlign: 'center',
    color: tokens.colors.bgWhite,
    fontSize: '11px',
    cursor: 'default',
  },
  cardContainer: {
    '& .ant-btn': {
      boxShadow: '0 0 0 rgba(0,0,0,0) !important',
    },
    minHeight: '160px',
    marginTop: `${tokens.spacing.sm}px`,
    padding: `${tokens.spacing.md}px ${tokens.spacing.md}px 13px ${tokens.spacing.md}px`,
    marginLeft: '7px',
    width: '99%',
    backgroundColor: tokens.colors.bgWhite,
    borderRadius: `${tokens.radius.md}px`,
    border: '1px solid #D9D9D9',
    position: 'relative',
  },
  colParent: {
    borderLeft: `1px solid ${tokens.colors.border}`,
    paddingLeft: '25px',
    height: '93px',
  },
  flex: {
    display: 'flex',
    flex: 1,
  },
  flexRow: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
  },
  flexCol: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
  confirmedDiv: {
    borderRadius: `${tokens.radius.md}px`,
    color: tokens.colors.bgWhite,
    fontSize: `${tokens.fontSize.small}px`,
    textAlign: 'center',
    width: '89px',
    backgroundColor: '#52C41A',
    border: '1px solid #389E0D',
  },
  enquiryNum: {
    color: '#1875F0',
    fontSize: `${tokens.fontSize.h3}px`,
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: '21px',
  },
  absoluteData: {
    position: 'absolute',
    top: `${tokens.spacing.lg}px`,
  },
  viewBidsStyle: {
    position: 'absolute',
    left: `${tokens.spacing.lg}px`,
    fontSize: `${tokens.fontSize.caption}px`,
    color: tokens.colors.primary,
  },
  spotSearch: {
    color: tokens.colors.textSecondary,
    textAlign: 'right',
  },
  requestedConnection: {
    marginTop: `${tokens.spacing.xs}px`,
    color: '#999999',
    fontStyle: 'italic',
    fontSize: `${tokens.fontSize.small}px`,
  },
  preSelectedBid: {
    color: tokens.colors.primary,
    fontSize: `${tokens.fontSize.small}px`,
    fontWeight: tokens.fontWeight.semibold,
    cursor: 'pointer',
  },
  connectionStatus: {
    justifyContent: 'flex-end',
    display: 'flex',
    alignItems: 'center',
  },
  rejectBid: {
    borderRadius: `${tokens.radius.md}px`,
    color: '#262626',
    fontSize: `${tokens.fontSize.small}px`,
    textAlign: 'center',
    width: '99px',
    backgroundColor: '#FFEBEB',
    border: '1px solid #F17678',
    display: 'inline-block'
  },
  rejectedTooltip: {
    maxWidth: '225px',
    fontSize: `${tokens.fontSize.caption}px`,
    lineHeight: '13px',
    textAlign: 'center',
    margin: `${tokens.spacing.xs}px`
  },
  remarkText: {
    width: '80%',
    textAlign: 'left',
    marginLeft: '10px'
  },
  rejectedText: {
    color: '#262626',
    marginTop: '10px',
    fontSize: `${tokens.fontSize.small}px`,
    fontWeight: tokens.fontWeight.semibold,
  },
  emptyContainerLocation: {
    color: '#666666',
    fontSize: `${tokens.fontSize.caption}px`,
    fontStyle: 'italic',
    padding: `${tokens.spacing.xs}px 0 0 14px`,
  },
  ellipsis: {
    width: '110px',
    ...commonStyles(theme).ellipses,
  },
  hiddenEllipsis: {
    width: '20%',
    ...commonStyles(theme).ellipses,
  },
  highValue: {
    position: 'absolute',
    zIndex: 1,
    backgroundColor: '#1990ff',
    height: '15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    top: '0px',
    left: '0px',
    fontSize: '8px',
    fontWeight: 'bold',
    color: 'white',
    borderRadius: '2px 2px 0px 0px'
  }
});

const COLORS = {
  GREEN: '#52C41A',
  RED: '#F5222D',
  BLUE: tokens.colors.primary,
  GREY: '#979797',
  YELLOW: '#FFD700'
};

type State = {
  viewBreakdownVisible: boolean;
  inquiryRejectModalVisible: boolean;
  isCardLoading: boolean;
  revokeInquiryModalVisible: boolean;
}

// Not everything is in interface Props, kindly add as things change
interface Props extends FormComponentProps {
  //from redux store
  currencySymbols: any;
  containerTypes: any;
  permission: boolean;
  timezone?: string;

  isInquiryFFReducerLoading: boolean;

  inquiryFfBidsRankingDatas: any;
  isLoadingInquiryFfBidsRankingData: boolean;

  inquiryFfBookingStatCountsData: {
    [id: string]: {
      totalBookings: number,
      successfulBookings: number,
      openBookings: number,
      cancelledBookings: number,
      declinedBookings: number,
    }
  };
  isLoadinginquiryFfBookingStatCountsData: boolean;


  //redux dispatch


  //from parent component
  //add as required
  cardData: any;
  handleReload: () => void;


  //react router
  history: any;


  //form, styling
  classes: {
    [X in keyof ReturnType<typeof styles>]: string;
  };
}

class InquiryFFCard extends React.PureComponent<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      viewBreakdownVisible: false,
      inquiryRejectModalVisible: false,
      isCardLoading: false,
      revokeInquiryModalVisible: false,
    };
  }


  handleViewBids = () => {
    const { history, cardData } = this.props;
    history.push(`${getInquiryDetailRoute('ff', cardData.id, 'bids')}`);
  }

  handleAddBid = (inquiryId) => {
    if (this.props.cardData?.isDeadlinePassed) return;
    sendEvent(
      EventCategories.INQUIRY_BIDDING,
      'Add Bid - Clicked',
      'Inquiry FF Card'
    );
    this.props.history.push(`${getInquiryFFRoute()}/createbid/${inquiryId}`);
  }

  handleClickInquiryNumber = () => {
    const { id } = this.props.cardData;
    this.props.history.push(getInquiryDetailRoute('ff', id, 'details'));
  }

  handleClickViewBreakdown = () => {
    this.setState({
      viewBreakdownVisible: true,
    });
  }

  closeViewBreakdown = () => {
    this.setState({
      viewBreakdownVisible: false,
    });
  }

  handleRejectInquiry = async (reasons, remarks) => {
    const { id: inquiryId } = this.props.cardData;
    this.setState({ isCardLoading: true });
    const response = await rejectInquiryFF({ inquiryId, reasons, remarks });
    if (response.isSuccess) {
      this.setState({ inquiryRejectModalVisible: false });
      this.props.handleReload();
    } else {
      message.error(response.errorMessage);
    }
    this.setState({ isCardLoading: false });
  }

  renderDot = (style, char) => {
    return <div style={{
      marginBottom: '3px', height: '8px', width: '8px',
      borderRadius: `${tokens.radius.md}px`, display: 'inline-block',
      verticalAlign: 'middle',
      ...style,
    }}><span style={{ paddingLeft: '1px' }}>{char}</span></div>;
  }

  renderLine = (color) => {
    return <div style={{ display: 'flex', flex: 1, height: '1px', backgroundColor: color }}>
    </div>;
  }

  renderStatus = () => {
    const { cardData } = this.props;
    const deadline = moment(cardData?.deadline);
    const currentTime = moment();
    const isDeadLinePassed = deadline.isBefore(currentTime);
    const status = cardData.status === 'OPEN' && isDeadLinePassed ? 'CLOSED' : cardData.status;
    let backgroundColor;
    let statusValue;
    switch (status) {
      case 'OPEN': {
        backgroundColor = COLORS.BLUE;
        statusValue = 'Open';
        break;
      }
      case 'CONFIRMED': {
        backgroundColor = COLORS.GREEN;
        statusValue = 'Confirmed';
        break;
      }
      case 'CLOSED': {
        backgroundColor = COLORS.RED;
        statusValue = 'Closed';
        break;
      }
      default: {
        backgroundColor: COLORS.BLUE;
        statusValue: 'Open';
        break;
      }
    }
    return (
      <div style={{ fontWeight: tokens.fontWeight.semibold, fontSize: `${tokens.fontSize.body}px`, color: '#262626', float: 'left' }}>
        {this.renderDot({
          backgroundColor,
          marginRight: '11px',
          marginLeft: '3px',
          verticalAlign: 'middle',
        }, '')}
        <span>{statusValue}</span>
      </div>)
      ;
  }

  getDeadline = () => {
    const { cardData: data, timezone } = this.props;
    const deadlineData = getDeadlineData(data.deadline, timezone);
    const { deadline, deadlineString, deadlineColor, isDeadlineNear } = deadlineData;
    if (!deadline) return;

    return <div style={{ fontSize: '11px', color: `${deadlineColor}` }}>
      <Tooltip title={isDeadlineNear ? deadline : ''}>
        <Icon type="hourglass" style={{ marginRight: '11px', fontSize: `${tokens.fontSize.body}px` }} />
        <span>{deadlineString}</span>
      </Tooltip>
    </div>;
  }

  isBidPlacementValid() {
    const { cardData: data } = this.props;
    const currentTime = new Date().getTime();
    const deadline = new Date(data.deadline).getTime();
    return currentTime < deadline;
  }

  renderDataCol1 = () => {
    const { classes, cardData: data } = this.props;
    return <div className={classes.flexCol}>
      <div style={{ marginBottom: '8px' }}>
        <a className={classes.enquiryNum}
          onClick={this.handleClickInquiryNumber}>{data.referenceNumber}</a>
      </div>
      {this.getDeadline()}
      <div style={{ marginBottom: '2px' }}>
        <Icon type="user" />
        <span style={{
          marginLeft: '10px', fontSize: `${tokens.fontSize.small}px`, fontWeight: 800,
          fontFamily: 'sans-serif', color: '#252323d9',
        }}>{data.shipperName}</span>
      </div>
    </div>;
  }

  getContainerType = (key) => {
    const { containerTypes } = this.props;
    if (!key || !containerTypes) return;
    const type = containerTypes.filter(item => item.key === key);
    if (type.length > 0) return type[0].label;
  }

  renderDataCol2 = () => {
    const { classes, cardData: data } = this.props;
    return <div className={classes.flexRow} style={{
      justifyContent: 'space-between',
      alignItems: 'center', height: '25px', marginTop: '4px',
      marginRight: '106px', width: '490px',
    }}>
      <div>
        {this.renderDot({ backgroundColor: COLORS.GREY }, '')}
        <div className={classNames(classes.absoluteData, classes.hiddenEllipsis)} style={{
          left: '22px',
        }}>
          {renderInquiryOriginDetails(classes, data)}
        </div>
      </div>
      {this.renderLine(COLORS.GREY)}
      <ContainerDetails data={data} />
      {this.renderLine(COLORS.GREY)}
      <div>
        {this.renderDot({ border: `1px solid ${COLORS.GREY}` }, '')}
        <br />
        <div className={classes.absoluteData}
          style={{
            right: (data.mode === InquiryModes.TRAILER || data.mode === InquiryModes.DOMESTIC) ? '20px' : '12px',
            ...((data.mode === InquiryModes.TRAILER || data.mode === InquiryModes.DOMESTIC)
              && {
              width: '20%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }
            )
          }}>
          {renderInquiryDestinationDetails(classes, data)}
        </div>
      </div>
    </div>
  }

  renderTitle = () => {
    const { classes, cardData: { rejectionReasons, rejectionRemarks } } = this.props;
    return (
      <div className={classes.rejectedTooltip}>
        <div style={{ display: 'flex', marginBottom: rejectionRemarks ? '12px' : 0 }}>
          <span>Reason</span>
          <div className={classes.remarkText}>
            <ol style={{ margin: '0px 6px', paddingLeft: '2px' }}>
              {rejectionReasons.map((reason, index) => {
                return (
                  <li key={index}>{reason}</li>
                )
              })}
            </ol>
          </div>
        </div>
        {
          isValidString(rejectionRemarks) && (
            <div style={{ display: 'flex' }}>
              <span>Remark</span>
              <div className={classes.remarkText}>{nl2Br(rejectionRemarks, '\n')}</div>
            </div>
          )
        }
      </div>
    )
  }

  renderDataCol4 = () => {
    const { classes, cardData, isLoadingInquiryFfBidsRankingData } = this.props;
    const bidRankData = this.props.inquiryFfBidsRankingDatas?.[this.props?.cardData?.id]?.[0]?.bidDetails;
    const { source, shipperName, currentPreSelectedBids } = cardData;
    const inquiryEditedAfterSent = cardData.editedAfterSent
    if (source === 'SPOT_SEARCH' && currentPreSelectedBids !== '0') {
      return (
        <div className={classes.spotSearch}>
          <strong>{shipperName}</strong> has requested for customised rate on your Offered Price.
        </div>
      );
    }
    if (isLoadingInquiryFfBidsRankingData) {
      return <div style={{ textAlign: 'center', width: '100%' }} >
        <Skeleton active title={false} paragraph={{ rows: 3 }} />
      </div>;
    }
    if (cardData.isRejected) {
      return (
        <div>
          <span className={classes.rejectBid}>
            Declined Inquiry
          </span>
          <Tooltip title={this.renderTitle()}>
            <Icon
              style={{ marginLeft: 5 }}
              theme="filled"
              type="info-circle"
            />
          </Tooltip>
          <div className={classes.rejectedText}>
            Please revoke and add bid incase your reason has been resolved.
          </div>
        </div>
      );
    }
    if (cardData.bidsSent === '0' && cardData.bidsSaved !== '0') {
      return <div style={{ fontSize: '15px', fontWeight: 600 }} >
        No bids have been sent.
      </div>;
    }
    if (!bidRankData?.bidId || (cardData.bidsSaved === '0' && cardData.bidsSent === '0')) {
      return <Col span={24}>
      <Row>
        {inquiryEditedAfterSent &&  <CardNotification
              inquiryId={cardData.id}
              notificationFlag={inquiryEditedAfterSent}
              handleBidAction = {false}
              mainText = {"This inquiry has been edited"}
            />
        }
      </Row>
        <Row>
          <div style={{ fontSize: '15px', fontWeight: 600 }} >
          No bids created yet.
        </div>
        </Row>
      </Col>
    }
    return (
      <>
        <Col span={24}>
          <Row>
            <CardNotification
              inquiryId={cardData.id}
              notificationFlag={cardData.notifyFfForReferenceRateChange}
              handleBidAction = {false}
              mainText = {"Reference Rate Updated."}
            />
          </Row>
          <Row>
            {bidRankData && bidRankData.isConfirmed ?
              <div className={classes.confirmedDiv}>
                Confirmed Bid
              </div>
              :
              <div style={{ fontSize: `${tokens.fontSize.caption}px` }}>
                {bidRankData && bidRankData.bidStatus}
              </div>
            }
          </Row>
          <Row>
            <Col span={12} >
              <div style={{ fontSize: `${tokens.fontSize.h3}px`, color: 'black' }}>
                <PricePerContainer
                  amount={bidRankData.totalPerUnit && bidRankData.totalPerUnit.amount}
                  currency={bidRankData.totalPerUnit && bidRankData.totalPerUnit.currency}
                  perLabel={bidRankData.totalPerUnit && bidRankData.totalPerUnit.format}
                  containerTypes={bidRankData.totalPerUnitType}
                  containerDetails={cardData.containerDetails ||
                    cardData.inquiryContainerDetails}
                  totalPrice={bidRankData.ffTotalWithoutTax}
                  freightPerUnit={bidRankData.freightPerUnit}
                  mode={cardData.mode}
                  sourceObjectType={'INQUIRY'} />
              </div>
            </Col>
            <Col span={12} >
              <div style={{ height: '40px' }}>
                <img src={bidRankData.shippingLineLogoUrl} />
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', margin: '3px 0px 0px'
              }}>
                <a style={{ fontSize: `${tokens.fontSize.caption}px`, color: tokens.colors.primary }}
                  onClick={this.handleClickViewBreakdown}>
                  View Breakdown</a>
                {this.state.viewBreakdownVisible && <ViewBreakdown
                  mode={cardData?.mode}
                  type={'FF'}
                  bidId={bidRankData?.bidId}
                  inquiryId={this.props.cardData.id}
                  visible={this.state.viewBreakdownVisible}
                  handleCancel={this.closeViewBreakdown}
                  handleOk={this.closeViewBreakdown} />
                }
              </div>
            </Col>
          </Row>
        </Col>
      </>
    );
  }

  renderRejectInquiry = () => {
    const { referenceNumber, deadline, status } = this.props.cardData;
    return (
      <React.Fragment>
        <a style={{
          marginRight: '8px', fontSize: `${tokens.fontSize.caption}px`,
          color: status === 'CLOSED' ? '#AAAAAA' : '#F84307',
          cursor: status === 'CLOSED' ? 'not-allowed' : 'pointer'
        }}
          onClick={status !== 'CLOSED' ? () => this.setState({ inquiryRejectModalVisible: true }) : undefined}
        >
          Decline Inquiry
        </a>
        <FFInquiryRejectModal
          loading={this.state.isCardLoading}
          visible={this.state.inquiryRejectModalVisible}
          handleCancel={this.handleCancel}
          deadlineDate={deadline}
          inquiryReferenceNumber={referenceNumber}
          handleRejectInquiry={this.handleRejectInquiry}
        />
      </React.Fragment>
    );
  }

  handleCancel = () => this.setState({ inquiryRejectModalVisible: false });

  handleRevokeInquiryCancel = () => this.setState({ revokeInquiryModalVisible: false });

  renderIcon = () => {
    const { classes, cardData, permission, inquiryFfBookingStatCountsData } = this.props;
    const { bidsSaved, bidsSent, status, isRejected, deadline, id } = cardData;

    const currentTime = new Date().getTime();
    // const isDeadLinePassed = currentTime - (deadline || Number.POSITIVE_INFINITY) >= 0 ? true : false
    const isDeadLinePassed = cardData?.isDeadlinePassed;
    const isDisabled = isDeadLinePassed || !permission;
    const { revokeInquiryModalVisible } = this.state;
    const statCounts = inquiryFfBookingStatCountsData?.[id];
    const showRejectInquiry = rejectInquiryShown(bidsSaved, bidsSent, isRejected);
    return (
      <div style={{ display: 'flex', alignItems: 'center', cursor: 'default', float: 'right' }}>
        {(statCounts && (isValidNumber(statCounts?.openBookings) || isValidNumber(statCounts?.successfulBookings))) &&
          <Tooltip title={<div
            style={{ display: 'flex', flexDirection: 'column' }}>
            <span> Bookings in Progress : {statCounts?.openBookings} </span>
            <span> Successful Bookings : {statCounts?.successfulBookings} </span>
          </div>}>
            <div className={classes.circle} style={{ marginRight: '10px' }}>
              {statCounts?.openBookings + statCounts?.successfulBookings}
            </div></Tooltip>}
        {showRejectInquiry && this.renderRejectInquiry()}
        {
          isRejected ? (
            <>
              <span style={{
                color: isDisabled ? '#AAAAAA' : tokens.colors.primary,
                fontSize: `${tokens.fontSize.small}px`,
                cursor: isDisabled ? 'not-allowed' : 'pointer'
              }}
                onClick={!isDisabled ? () => this.setState({ revokeInquiryModalVisible: true }) : undefined}>
                Revoke & Add Bid
              </span>
              <FFRevokeInquiryModal
                visible={revokeInquiryModalVisible}
                handleCancel={this.handleRevokeInquiryCancel}
                handleAddBid={() => this.handleAddBid(cardData.id)}
              />
            </>
          ) : (
            <Button type="primary" size="small"
              onClick={!isDisabled ? () => this.handleAddBid(cardData.id) : undefined}
              style={{
                background: (isDisabled ? '#D9D9D9' : COLORS.BLUE),
                fontSize: `${tokens.fontSize.caption}px`,
                border: tokens.colors.primary,
                pointerEvents: (isDisabled ? 'none' : undefined),
                cursor: (isDisabled ? 'pointer' : undefined),
              }}>
              <span style={{ lineHeight: '18px', marginRight: '-5px', textShadow: 'none' }}>
                Add Bid</span>
              <Icon type="plus-circle" style={{ fontSize: `${tokens.fontSize.small}px` }} />
            </Button>
          )
        }
      </div>
    );
  }

  renderDataCol3 = () => {
    const { classes, cardData: data } = this.props;
    return (
      <div style={{ width: '100%' }}>
        <Row>
          <Col span={11} style={{ fontSize: `${tokens.fontSize.small}px` }}>Bids Saved</Col>
          <Col span={13} style={{ color: 'black', lineHeight: '16px' }}>
            <span style={{ fontWeight: 600 }}>{data.bidsSaved}</span>
          </Col>
        </Row>
        <Row>
          <Col span={11} style={{ fontSize: `${tokens.fontSize.small}px` }}>Bids Sent</Col>
          <Col span={13} style={{
            fontWeight: tokens.fontWeight.semibold, color: 'black', lineHeight: '16px',
          }}>
            {data.bidsSent}</Col>
        </Row>
        {data.bidsRejected > 0 &&
          <Row>
            <Col span={11} style={{ fontWeight: tokens.fontWeight.semibold, color: '#F5222D', fontSize: `${tokens.fontSize.small}px` }}>Bids Rejected</Col>
            <Col span={13} style={{
              fontWeight: tokens.fontWeight.semibold, color: '#F5222D', lineHeight: '16px',
            }}>
              {data.bidsRejected}</Col>
          </Row>}
        <Row style={{ position: 'absolute', bottom: '0' }}>
          <div style={{ fontSize: `${tokens.fontSize.body}px`, color: 'black', fontWeight: 600 }}>
            {data.incoterm ? data.incoterm : ''} {data.pickupAndDrop ?
              `(${data.pickupAndDrop})` : ''}
          </div>
        </Row>
      </div>
    );
  }

  renderColumn = (span, elem, renderBorder = true) => {
    const { classes } = this.props;
    return <Col span={span} style={{ display: 'flex', flex: 1 }}
      className={renderBorder ? classes.colParent : undefined}>
      {elem}
    </Col>;
  }

  renderViewPreselectedBids = () => {
    const { classes } = this.props;
    return (
      <div className={classes.preSelectedBid} onClick={this.handleViewBids}>
        View Pre-selected Bids
      </div>
    );
  };

  render() {
    const { cardData, classes } = this.props;
    const { source, currentPreSelectedBids } = cardData;
    const status = cardData.status;
    const inquiryEditedAfterSent = cardData.editedAfterSent
    return (
      <div style={(status === 'CONFIRMED') ? { border: `1px solid ${COLORS.GREEN}` } : 
      inquiryEditedAfterSent ? { border: `1px solid ${COLORS.YELLOW}` } : undefined}
        className={classes.cardContainer} key={cardData.id}>
        {cardData?.otherDetails?.isHighValueInsuranceRequired ? <div className={classes.highValue}>High Value Cargo</div> : null}
        <Row >
          {this.renderColumn(4, this.renderDataCol1(), false)}
          {this.renderColumn(9, this.renderDataCol2())}
          {this.renderColumn(5, this.renderDataCol3())}
          {this.renderColumn(6, this.renderDataCol4())}
        </Row>
        <Row>
          <Col span={4}>{this.renderStatus()}</Col>
          <Col span={9} className={classes.emptyContainerLocation}>
            {cardData.mode === InquiryModes.TRAILER &&
              cardData.emptyContainerLocationPortDetails?.code !== cardData.destinationPortDetails?.code &&
              getEmptyContainerLocation(cardData.type, cardData.emptyContainerLocationPortDetails?.name)}
          </Col>
          <Col span={11}>{source === 'SPOT_SEARCH' && currentPreSelectedBids !== '0' ?
            <div className={classes.connectionStatus}>
              {this.renderViewPreselectedBids()}
            </div> : this.renderIcon()}</Col>
        </Row>
      </div>);
  }
}

const mapStateToProps = (state) => {
  const {
    masterData: { currencySymbols, permissions, timezone },
    staticData: { containerTypesKeyVal: containerTypes },
    inquiryFFReducer,
  } = state;
  return {
    currencySymbols,
    containerTypes,
    permission: permissions.INQUIRY_BIDDING_ALL_ACTIONS,
    timezone,
    isInquiryFFReducerLoading: state?.inquiryFFReducer?.isLoading,

    inquiryFfBidsRankingDatas: inquiryFFReducer.inquiryFfBidsRankingData,
    isLoadingInquiryFfBidsRankingData: inquiryFFReducer.isLoadingInquiryFfBidsRankingData,

    inquiryFfBookingStatCountsData: inquiryFFReducer.inquiryFfBookingStatCountsData,
    isLoadinginquiryFfBookingStatCountsData: inquiryFFReducer.isLoadinginquiryFfBookingStatCountsData,
  };
};
const InquiryFFCardStyled = injectSheet(styles)(InquiryFFCard);
export default withRouter(connect(mapStateToProps)(InquiryFFCardStyled));
